import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { useAuth } from '@/context/AuthContext';

const WalkthroughContext = createContext(null);

export function WalkthroughProvider({ children }) {
  const { user } = useAuth();
  const [statuses, setStatuses] = useState({});
  const [activeTour, setActiveTour] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (user?.id) {
      api.get('/walkthroughs/status')
        .then(r => setStatuses(r.data.statuses || {}))
        .catch(() => {})
        .finally(() => setLoaded(true));
    }
  }, [user?.id]);

  const shouldShowTour = useCallback((moduleId) => {
    if (!loaded) return false;
    const s = statuses[moduleId];
    if (!s) return true;
    return !s.do_not_show;
  }, [statuses, loaded]);

  const startTour = useCallback((moduleId, steps) => {
    setActiveTour({ moduleId, steps });
    setCurrentStep(0);
  }, []);

  const nextStep = useCallback(() => {
    if (!activeTour) return;
    if (currentStep < activeTour.steps.length - 1) {
      setCurrentStep(p => p + 1);
    } else {
      completeTour();
    }
  }, [activeTour, currentStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) setCurrentStep(p => p - 1);
  }, [currentStep]);

  const completeTour = useCallback(() => {
    if (!activeTour) return;
    api.post(`/walkthroughs/${activeTour.moduleId}/complete`).catch(() => {});
    setStatuses(p => ({ ...p, [activeTour.moduleId]: { completed: true, skipped: false, do_not_show: true } }));
    setActiveTour(null);
    setCurrentStep(0);
  }, [activeTour]);

  const skipTour = useCallback(() => {
    if (!activeTour) return;
    api.post(`/walkthroughs/${activeTour.moduleId}/skip`).catch(() => {});
    setStatuses(p => ({ ...p, [activeTour.moduleId]: { skipped: true, do_not_show: true } }));
    setActiveTour(null);
    setCurrentStep(0);
  }, [activeTour]);

  const resetTour = useCallback((moduleId) => {
    api.post(`/walkthroughs/${moduleId}/reset`).catch(() => {});
    setStatuses(p => ({ ...p, [moduleId]: { completed: false, skipped: false, do_not_show: false } }));
  }, []);

  return (
    <WalkthroughContext.Provider value={{
      statuses, loaded, activeTour, currentStep,
      shouldShowTour, startTour, nextStep, prevStep,
      completeTour, skipTour, resetTour,
      userRole: user?.role || 'user',
    }}>
      {children}
    </WalkthroughContext.Provider>
  );
}

export function useWalkthroughContext() {
  return useContext(WalkthroughContext);
}
